import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LifeBuoy, Lock, Pencil, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSupportSession } from "@/contexts/SupportSessionContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Permanent Support Mode banner. Renders nothing at all unless a Super Admin
 * has explicitly started a support session.
 */
export function SupportModeBanner() {
  const { session, isReadOnly, enableEditing, endSession, logEvent } = useSupportSession();
  const { pathname } = useLocation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exiting, setExiting] = useState(false);

  // Audit every page visited during the session
  useEffect(() => {
    if (!session) return;
    logEvent({ type: "page_view", path: pathname });
  }, [session?.id, pathname, logEvent, session]);

  if (!session) return null;

  const started = new Date(session.startedAt);

  return (
    <>
      <div className="sticky top-0 z-[60] w-full border-b-2 border-orange-500 bg-orange-500/95 text-white shadow-md print:hidden">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-6 gap-y-2 px-3 py-2 sm:px-5">
          <div className="flex items-center gap-2 font-semibold">
            <LifeBuoy className="h-4 w-4" />
            <span className="text-sm tracking-wide">SUPPORT MODE</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs sm:text-sm">
            <span>
              <span className="opacity-80">Viewing: </span>
              <strong>{session.companyName || "Unknown company"}</strong>
            </span>
            <span className="hidden sm:inline">
              <span className="opacity-80">Logged in as: </span>
              <strong>Super Admin</strong>
            </span>
            <span className="flex items-center gap-1">
              <span className="opacity-80">Mode: </span>
              {isReadOnly ? (
                <strong className="flex items-center gap-1">
                  <Lock className="h-3.5 w-3.5" /> Read Only
                </strong>
              ) : (
                <strong className="flex items-center gap-1">
                  <Pencil className="h-3.5 w-3.5" /> Editing Enabled
                </strong>
              )}
            </span>
            <span className="hidden md:inline">
              <span className="opacity-80">Session started: </span>
              <strong>
                {started.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
              </strong>
            </span>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {isReadOnly && (
              <Button
                size="sm"
                variant="secondary"
                className="h-7 bg-white text-orange-700 hover:bg-white/90"
                onClick={() => setConfirmOpen(true)}
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Enable Editing
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={exiting}
              className="h-7 border-white/70 bg-transparent text-white hover:bg-white/15 hover:text-white"
              onClick={async () => {
                setExiting(true);
                await endSession();
              }}
            >
              <LogOut className="mr-1.5 h-3.5 w-3.5" />
              Exit Support Session
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable editing?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to make live changes to this customer's account. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await enableEditing();
                setConfirmOpen(false);
              }}
            >
              Yes, enable editing
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
